import { useState, useEffect, useRef } from "react";

const capabilities = [
  {
    index: "01",
    title: "Workflow Systems",
    description:
      "Internal dashboards, CRMs, lead pipelines, operational workflows, and coordination systems for businesses running on spreadsheets and fragmented tools.",
    tag: "Internal Tools",
  },
  {
    index: "02",
    title: "Marketplace Platforms",
    description:
      "Booking systems, multi-vendor marketplaces, event-tech products, and coordination-heavy platforms built for operational scale.",
    tag: "Platform Engineering",
  },
  {
    index: "03",
    title: "Financial Infrastructure",
    description:
      "Quant trading systems, crypto execution engines, backtesting frameworks, risk infrastructure, and data-intensive financial platforms — built for real latency and capital constraints.",
    tag: "FinTech",
  },
];

const industries = [
  {
    title: "Finance & Trading",
    points: [
      "Algo & quant trading systems",
      "Crypto execution infrastructure",
      "Backtesting & strategy research",
      "Risk & portfolio dashboards",
    ],
  },
  {
    title: "Event & Hospitality",
    points: [
      "Venue operations",
      "Lead management systems",
      "Vendor coordination",
      "Booking workflows",
    ],
  },
  {
    title: "Operational SMEs",
    points: [
      "Internal tools",
      "Workflow automation",
      "Business dashboards",
      "Process visibility",
    ],
  },
];

const work = [
  {
    year: "2025",
    title: "Algorithmic Trading Infrastructure",
    outcome: "<500ms",
    outcomeLabel: "execution target",
    description:
      "Designed a modular trading and backtesting platform with multi-account execution architecture, realistic slippage modeling, and configurable strategy layers.",
  },
  {
    year: "2024",
    title: "Operational Real Estate Platform",
    outcome: "30%",
    outcomeLabel: "faster APIs",
    description:
      "Optimized Rails backend systems, asynchronous workflows, and operational dashboards for a real estate platform serving 10,000+ monthly users.",
  },
  {
    year: "2023",
    title: "Health Analytics Infrastructure",
    outcome: "100+",
    outcomeLabel: "data points / user",
    description:
      "Built scalable APIs and analytics systems integrating Apple Health and Google Health data into predictive health workflows.",
  },
];

const ecosystem = [
  {
    title: "Favyt Technologies",
    subtitle: "Operational Systems Studio",
    description:
      "Founder-led consulting focused on workflow systems, marketplaces, dashboards, and scalable backend infrastructure.",
  },
  {
    title: "Favyt Labs",
    subtitle: "Quant Lab · Crypto Infra · Experimental",
    description:
      "Public quant research, crypto execution infrastructure, experimental trading strategies, and open-source tooling — built in the open at favyt.in.",
  },
  {
    title: "VenueExchange",
    subtitle: "Event-Tech Initiative",
    description:
      "A platform focused on simplifying venue discovery, coordination, and operational workflows within India's fragmented event ecosystem.",
  },
];

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.12 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, className = "" }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

export default function FavytLandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", type: "", message: "" });

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#080C14",
        color: "#E8E8E4",
        fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
        overflowX: "hidden",
        position: "relative",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,300&family=DM+Serif+Display:ital@0;1&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --orange: #E85D04;
          --orange-light: #F48C06;
          --orange-dim: rgba(232, 93, 4, 0.15);
          --orange-glow: rgba(232, 93, 4, 0.08);
          --surface: rgba(255,255,255,0.035);
          --surface-hover: rgba(255,255,255,0.055);
          --border: rgba(255,255,255,0.08);
          --border-hover: rgba(255,255,255,0.15);
          --text-muted: #6B7280;
          --text-secondary: #9CA3AF;
        }

        html { scroll-behavior: smooth; }

        .serif { font-family: 'DM Serif Display', Georgia, serif; }

        .nav-link {
          font-size: 13px;
          color: var(--text-secondary);
          text-decoration: none;
          letter-spacing: 0.02em;
          transition: color 0.2s;
          padding: 4px 0;
          position: relative;
        }
        .nav-link::after {
          content: '';
          position: absolute;
          bottom: 0; left: 0;
          width: 0; height: 1px;
          background: var(--orange);
          transition: width 0.3s ease;
        }
        .nav-link:hover { color: #E8E8E4; }
        .nav-link:hover::after { width: 100%; }

        .btn-primary {
          display: inline-flex; align-items: center; gap: 8px;
          background: var(--orange);
          color: #fff;
          border: none;
          padding: 14px 28px;
          border-radius: 100px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s, transform 0.15s;
          font-family: inherit;
          text-decoration: none;
          letter-spacing: 0.01em;
        }
        .btn-primary:hover { background: var(--orange-light); transform: translateY(-1px); }
        .btn-primary:active { transform: translateY(0); }

        .btn-secondary {
          display: inline-flex; align-items: center; gap: 8px;
          background: transparent;
          color: #E8E8E4;
          border: 1px solid var(--border-hover);
          padding: 14px 28px;
          border-radius: 100px;
          font-size: 14px;
          font-weight: 400;
          cursor: pointer;
          transition: border-color 0.2s, background 0.2s, transform 0.15s;
          font-family: inherit;
          text-decoration: none;
          letter-spacing: 0.01em;
        }
        .btn-secondary:hover { border-color: var(--orange); background: var(--orange-dim); transform: translateY(-1px); }

        .eyebrow {
          display: inline-flex; align-items: center; gap: 10px;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.25em;
          text-transform: uppercase;
          color: var(--orange);
        }
        .eyebrow::before {
          content: '';
          width: 24px; height: 1px;
          background: var(--orange);
          display: block;
        }

        .glass-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 20px;
          backdrop-filter: blur(12px);
          transition: border-color 0.3s, background 0.3s, transform 0.3s;
        }
        .glass-card:hover {
          border-color: var(--border-hover);
          background: var(--surface-hover);
          transform: translateY(-2px);
        }

        .stat-value {
          font-family: 'DM Serif Display', Georgia, serif;
          font-size: clamp(2.5rem, 5vw, 4rem);
          color: var(--orange);
          line-height: 1;
          letter-spacing: -0.02em;
        }

        .industry-point {
          display: flex; align-items: flex-start; gap: 12px;
          padding: 14px 0;
          border-top: 1px solid rgba(255,255,255,0.06);
          font-size: 14px;
          color: var(--text-secondary);
          transition: color 0.2s;
        }
        .industry-point:hover { color: #E8E8E4; }

        .work-row {
          display: grid;
          grid-template-columns: 80px 1fr auto;
          gap: 32px;
          padding: 36px 0;
          border-top: 1px solid var(--border);
          align-items: start;
          transition: background 0.2s;
        }
        .work-row:last-child { border-bottom: 1px solid var(--border); }
        @media (max-width: 768px) {
          .work-row { grid-template-columns: 1fr; gap: 16px; }
        }

        .form-input {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 16px 20px;
          font-size: 14px;
          color: #E8E8E4;
          font-family: inherit;
          outline: none;
          transition: border-color 0.2s, background 0.2s;
        }
        .form-input::placeholder { color: var(--text-muted); }
        .form-input:focus {
          border-color: var(--orange);
          background: rgba(232,93,4,0.04);
        }

        .noise-overlay {
          position: fixed; inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
          pointer-events: none;
          z-index: 1;
          opacity: 0.4;
        }

        .section-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--border), transparent);
        }

        .tag-pill {
          display: inline-block;
          background: var(--orange-dim);
          color: var(--orange-light);
          border: 1px solid rgba(232,93,4,0.25);
          border-radius: 100px;
          padding: 4px 12px;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .ambient-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          pointer-events: none;
        }

        @media (max-width: 900px) {
          .hero-grid { grid-template-columns: 1fr !important; }
          .stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .industries-grid { grid-template-columns: 1fr !important; }
          .ecosystem-grid { grid-template-columns: 1fr !important; }
          .contact-grid { grid-template-columns: 1fr !important; }
          .insights-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div className="noise-overlay" />

      {/* NAVBAR */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          padding: "0 32px",
          transition: "background 0.3s, border-color 0.3s",
          background: scrolled ? "rgba(8,12,20,0.92)" : "transparent",
          borderBottom: scrolled ? "1px solid var(--border)" : "1px solid transparent",
          backdropFilter: scrolled ? "blur(20px)" : "none",
        }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
          <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em", color: "#E8E8E4" }}>
            Favyt<span style={{ color: "var(--orange)" }}>.</span>
          </div>

          <nav style={{ display: "flex", gap: 36, alignItems: "center" }}>
            {["Capabilities", "Industries", "Work", "Ecosystem", "Contact"].map((item) => (
              <a key={item} href={`#${item.toLowerCase()}`} className="nav-link" style={{ display: window?.innerWidth < 768 ? "none" : undefined }}>
                {item}
              </a>
            ))}
            <a href="#contact" className="btn-primary" style={{ padding: "10px 20px", fontSize: 13 }}>
              Book a call
            </a>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section style={{ position: "relative", overflow: "hidden", paddingBottom: 0 }}>
        <div className="ambient-orb" style={{ width: 600, height: 600, background: "rgba(232,93,4,0.07)", top: -200, left: -100 }} />
        <div className="ambient-orb" style={{ width: 400, height: 400, background: "rgba(244,140,6,0.05)", top: 100, right: -50 }} />

        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "80px 32px 0" }}>
          <Reveal>
            <div className="eyebrow" style={{ marginBottom: 28 }}>Favyt Technologies LLP</div>
          </Reveal>

          <div className="hero-grid" style={{ display: "grid", gridTemplateColumns: "1.15fr 0.85fr", gap: 48, alignItems: "start" }}>
            <div>
              <Reveal delay={0.1}>
                <h1
                  className="serif"
                  style={{
                    fontSize: "clamp(2.8rem, 5.5vw, 4.5rem)",
                    fontWeight: 400,
                    lineHeight: 1.08,
                    letterSpacing: "-0.02em",
                    color: "#F0EFE9",
                    maxWidth: 680,
                  }}
                >
                  Operational software systems for businesses where{" "}
                  <em style={{ color: "var(--orange)", fontStyle: "italic" }}>workflows matter.</em>
                </h1>
              </Reveal>

              <Reveal delay={0.2}>
                <p style={{ marginTop: 28, fontSize: 17, lineHeight: 1.7, color: "var(--text-secondary)", maxWidth: 520 }}>
                  We design and build internal platforms, marketplaces, dashboards, and workflow systems for finance, event-tech, and coordination-heavy businesses.
                </p>
              </Reveal>

              <Reveal delay={0.3}>
                <div style={{ display: "flex", gap: 14, marginTop: 40, flexWrap: "wrap" }}>
                  <a href="#contact" className="btn-primary">
                    Book a discovery call
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                  </a>
                  <a href="#work" className="btn-secondary">View selected work</a>
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.15}>
              <div
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 24,
                  padding: 36,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div style={{ position: "absolute", top: 0, right: 0, width: 200, height: 200, background: "radial-gradient(circle, rgba(232,93,4,0.12) 0%, transparent 70%)" }} />
                <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.25em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 32 }}>
                  What we optimize
                </div>

                <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>
                  {[
                    { value: "<500ms", label: "Low-latency execution targets for financial systems" },
                    { value: "30%", label: "API performance improvements on production Rails systems" },
                    { value: "6+", label: "Years building scalable operational software" },
                    { value: "B2B", label: "Workflow systems for operationally complex businesses" },
                  ].map((stat, i) => (
                    <div key={i}>
                      <div className="stat-value">{stat.value}</div>
                      <div style={{ marginTop: 10, fontSize: 13, lineHeight: 1.6, color: "var(--text-muted)" }}>{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* scroll indicator line */}
        <div style={{ maxWidth: 1200, margin: "60px auto 0", padding: "0 32px" }}>
          <div className="section-divider" />
        </div>
      </section>

      {/* PROBLEM STATEMENT */}
      <section style={{ padding: "80px 32px" }}>
        <div style={{ maxWidth: 800, margin: "0 auto", textAlign: "center" }}>
          <Reveal>
            <div className="eyebrow" style={{ justifyContent: "center", marginBottom: 24 }}>Operational Reality</div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2
              className="serif"
              style={{ fontSize: "clamp(1.9rem, 3.5vw, 2.9rem)", fontWeight: 400, lineHeight: 1.2, letterSpacing: "-0.02em", color: "#F0EFE9" }}
            >
              Most growing businesses still run on spreadsheets, WhatsApp, and fragmented workflows.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p style={{ marginTop: 24, fontSize: 16, lineHeight: 1.8, color: "var(--text-secondary)" }}>
              We help operational businesses centralize visibility, automate coordination, and replace manual systems with scalable software infrastructure.
            </p>
          </Reveal>
        </div>
      </section>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px" }}>
        <div className="section-divider" />
      </div>

      {/* CAPABILITIES */}
      <section id="capabilities" style={{ padding: "80px 32px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <Reveal>
            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 56, flexWrap: "wrap", gap: 16 }}>
              <div>
                <div className="eyebrow" style={{ marginBottom: 16 }}>Capabilities</div>
                <h2 className="serif" style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)", fontWeight: 400, letterSpacing: "-0.02em", color: "#F0EFE9" }}>
                  What we build
                </h2>
              </div>
            </div>
          </Reveal>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            {capabilities.map((cap, i) => (
              <Reveal key={cap.title} delay={i * 0.1}>
                <div
                  className="glass-card"
                  style={{ padding: 32, height: "100%", cursor: "default" }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)", letterSpacing: "0.1em" }}>{cap.index}</span>
                    <span className="tag-pill">{cap.tag}</span>
                  </div>
                  <h3 style={{ fontSize: 22, fontWeight: 500, letterSpacing: "-0.02em", color: "#F0EFE9", marginBottom: 16 }}>{cap.title}</h3>
                  <p style={{ fontSize: 14, lineHeight: 1.7, color: "var(--text-secondary)" }}>{cap.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section id="industries" style={{ padding: "80px 32px", background: "rgba(255,255,255,0.015)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <Reveal>
            <div style={{ marginBottom: 56, maxWidth: 600 }}>
              <div className="eyebrow" style={{ marginBottom: 16 }}>Industry Focus</div>
              <h2 className="serif" style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)", fontWeight: 400, letterSpacing: "-0.02em", color: "#F0EFE9", lineHeight: 1.2 }}>
                Deep systems work across finance, event-tech, and operational businesses.
              </h2>
            </div>
          </Reveal>

          <div className="industries-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            {industries.map((ind, i) => (
              <Reveal key={ind.title} delay={i * 0.1}>
                <div
                  className="glass-card"
                  style={{ padding: 32, height: "100%" }}
                >
                  <h3 style={{ fontSize: 20, fontWeight: 500, color: "#F0EFE9", marginBottom: 8 }}>{ind.title}</h3>
                  <div style={{ width: 32, height: 2, background: "var(--orange)", borderRadius: 2, marginBottom: 24 }} />
                  <div>
                    {ind.points.map((pt, j) => (
                      <div key={pt} className="industry-point" style={{ animationDelay: `${j * 0.05}s` }}>
                        <span style={{ color: "var(--orange)", fontSize: 12, marginTop: 2, flexShrink: 0 }}>→</span>
                        <span>{pt}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WORK */}
      <section id="work" style={{ padding: "80px 32px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <Reveal>
            <div style={{ marginBottom: 56 }}>
              <div className="eyebrow" style={{ marginBottom: 16 }}>Selected Work</div>
              <h2 className="serif" style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)", fontWeight: 400, letterSpacing: "-0.02em", color: "#F0EFE9" }}>
                Real operational systems with measurable outcomes.
              </h2>
            </div>
          </Reveal>

          <div>
            {work.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.1}>
                <div className="work-row">
                  <div style={{ paddingTop: 4 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, letterSpacing: "0.15em", color: "var(--text-muted)", textTransform: "uppercase" }}>
                      {item.year}
                    </div>
                  </div>
                  <div>
                    <h3 style={{ fontSize: 20, fontWeight: 500, color: "#F0EFE9", letterSpacing: "-0.01em", marginBottom: 12 }}>{item.title}</h3>
                    <p style={{ fontSize: 14, lineHeight: 1.75, color: "var(--text-secondary)", maxWidth: 560 }}>{item.description}</p>
                  </div>
                  <div style={{ textAlign: "right", paddingTop: 4 }}>
                    <div className="serif" style={{ fontSize: "clamp(2rem, 3vw, 2.8rem)", color: "var(--orange)", lineHeight: 1, letterSpacing: "-0.03em" }}>
                      {item.outcome}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6, letterSpacing: "0.05em" }}>{item.outcomeLabel}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ECOSYSTEM */}
      <section id="ecosystem" style={{ padding: "80px 32px", background: "rgba(255,255,255,0.015)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <Reveal>
            <div style={{ marginBottom: 56, maxWidth: 600 }}>
              <div className="eyebrow" style={{ marginBottom: 16 }}>Ecosystem</div>
              <h2 className="serif" style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)", fontWeight: 400, letterSpacing: "-0.02em", color: "#F0EFE9", lineHeight: 1.2 }}>
                One operational systems foundation. Three focused initiatives.
              </h2>
            </div>
          </Reveal>

          <div className="ecosystem-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            {ecosystem.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.1}>
                <div
                  className="glass-card"
                  style={{ padding: 36, height: "100%", position: "relative", overflow: "hidden" }}
                >
                  <div style={{ position: "absolute", top: -40, right: -40, width: 120, height: 120, background: "radial-gradient(circle, var(--orange-glow) 0%, transparent 70%)", borderRadius: "50%" }} />
                  <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--orange)", marginBottom: 16 }}>{item.subtitle}</div>
                  <h3 style={{ fontSize: 24, fontWeight: 500, letterSpacing: "-0.02em", color: "#F0EFE9", marginBottom: 16 }}>{item.title}</h3>
                  <p style={{ fontSize: 14, lineHeight: 1.75, color: "var(--text-secondary)" }}>{item.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INSIGHTS */}
      <section style={{ padding: "80px 32px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div className="insights-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center" }}>
            <Reveal>
              <div>
                <div className="eyebrow" style={{ marginBottom: 20 }}>Ideas & Thinking</div>
                <h2 className="serif" style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)", fontWeight: 400, letterSpacing: "-0.02em", color: "#F0EFE9", lineHeight: 1.2 }}>
                  Operational insights shared publicly.
                </h2>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div style={{ borderLeft: "1px solid var(--border)", paddingLeft: 40 }}>
                <p style={{ fontSize: 16, lineHeight: 1.8, color: "var(--text-secondary)", marginBottom: 20 }}>
                  We regularly share thoughts on workflow systems, backend architecture, operational bottlenecks, marketplace design, and scaling coordination-heavy businesses.
                </p>
                <p style={{ fontSize: 16, lineHeight: 1.8, color: "var(--text-secondary)", marginBottom: 32 }}>
                  Most businesses don't need more tools. They need clearer operational visibility.
                </p>
                <a
                  href="https://www.linkedin.com/company/favyt"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                  style={{ display: "inline-flex" }}
                >
                  Connect on LinkedIn
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"/></svg>
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px" }}>
        <div className="section-divider" />
      </div>

      {/* CONTACT */}
      <section id="contact" style={{ padding: "80px 32px 100px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div className="contact-grid" style={{ display: "grid", gridTemplateColumns: "1fr 0.9fr", gap: 80, alignItems: "start" }}>
            <Reveal>
              <div>
                <div className="eyebrow" style={{ marginBottom: 20 }}>Contact</div>
                <h2
                  className="serif"
                  style={{ fontSize: "clamp(2.2rem, 4vw, 3.5rem)", fontWeight: 400, letterSpacing: "-0.03em", color: "#F0EFE9", lineHeight: 1.1, marginBottom: 28 }}
                >
                  Let's build operational systems that scale.
                </h2>
                <p style={{ fontSize: 15, lineHeight: 1.75, color: "var(--text-secondary)", maxWidth: 400 }}>
                  Whether you're replacing a spreadsheet, scaling an existing system, or building something new — we start with a focused discovery conversation.
                </p>

                <div style={{ marginTop: 48, display: "flex", flexDirection: "column", gap: 20 }}>
                  {[
                    { label: "Response time", value: "Within 24 hours" },
                    { label: "Engagement model", value: "Project-based & retainer" },
                    { label: "Focus", value: "Finance, event-tech, SMEs" },
                  ].map((item) => (
                    <div key={item.label} style={{ display: "flex", justifyContent: "space-between", paddingBottom: 16, borderBottom: "1px solid var(--border)" }}>
                      <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{item.label}</span>
                      <span style={{ fontSize: 13, color: "#E8E8E4", fontWeight: 500 }}>{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid var(--border)",
                  borderRadius: 24,
                  padding: 40,
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <input
                    className="form-input"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                  <input
                    className="form-input"
                    placeholder="Email address"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                  <input
                    className="form-input"
                    placeholder="Project type"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  />
                  <textarea
                    className="form-input"
                    rows={5}
                    placeholder="Tell us about your operational challenge"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    style={{ resize: "vertical" }}
                  />
                  <button className="btn-primary" style={{ width: "100%", justifyContent: "center", padding: "16px 28px", borderRadius: 12, fontSize: 15 }}>
                    Start the conversation
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                  </button>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ borderTop: "1px solid var(--border)", padding: "32px", background: "rgba(0,0,0,0.3)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: "-0.02em" }}>
            Favyt<span style={{ color: "var(--orange)" }}>.</span>
          </div>
          <div style={{ display: "flex", gap: 32 }}>
            {["Capabilities", "Industries", "Work", "Contact"].map((item) => (
              <a key={item} href={`#${item.toLowerCase()}`} style={{ fontSize: 13, color: "var(--text-muted)", textDecoration: "none", transition: "color 0.2s" }}
                onMouseEnter={e => e.target.style.color = "#E8E8E4"}
                onMouseLeave={e => e.target.style.color = "var(--text-muted)"}
              >
                {item}
              </a>
            ))}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>© 2026 Favyt Technologies LLP</div>
        </div>
      </footer>
    </div>
  );
}
